import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  getConteudoChave,
  putConteudoChave,
  CONTEUDO_CHAVES,
  getServicos,
  createServico,
  updateServico,
  deleteServico,
  type Servico,
} from '@/api/adminApi'
import { AdminPage, AdminPanel } from '@/components/Admin'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

function parseIntro(raw: string | null): string {
  if (!raw?.trim()) return ''
  try {
    const j = JSON.parse(raw) as { intro?: string }
    if (typeof j.intro === 'string') return j.intro
  } catch {
    /* plain text */
  }
  return raw
}

const FORM_EMPTY = {
  nome: '',
  descricao: '',
  categoria: '',
  icone: '',
  ativo: true,
  ordem: 0,
  preco: '',
  duracao_minutos: '60',
}

function formatPrecoDisplay(preco: number | string | null | undefined): string {
  if (preco == null || preco === '') return ''
  const n = Number(preco)
  if (!Number.isFinite(n)) return String(preco)
  return n.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function AdminServicosPage() {
  const [intro, setIntro] = useState('')
  const [items, setItems] = useState<Servico[]>([])
  const [categorias, setCategorias] = useState<Array<{ nome: string }>>([])
  const [loading, setLoading] = useState(true)
  const [savingIntro, setSavingIntro] = useState(false)
  const [savingCatalog, setSavingCatalog] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Servico | null>(null)
  const [form, setForm] = useState(FORM_EMPTY)
  const { notifySaved, notifyDeleted, notifyError, notifyValidation } = useSaveFeedback()

  const load = () => {
    setError(null)
    Promise.all([
      getConteudoChave(CONTEUDO_CHAVES.servicos_intro),
      getServicos(true),
      getConteudoChave(CONTEUDO_CHAVES.categorias).catch(() => null),
    ])
      .then(([introRaw, servicos, catRaw]) => {
        setIntro(parseIntro(introRaw ?? null))
        setItems(servicos)
        if (catRaw?.trim()) {
          try {
            const arr = JSON.parse(catRaw) as unknown[]
            if (Array.isArray(arr)) {
              setCategorias(
                arr
                  .filter((x): x is { nome?: string } => x != null && typeof x === 'object')
                  .map((x) => ({ nome: String(x.nome ?? '').trim() }))
                  .filter((x) => x.nome)
              )
            }
          } catch {
            /* ignore */
          }
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Erro ao carregar'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const handleSalvarIntro = async (e: React.FormEvent) => {
    e.preventDefault()
    setSavingIntro(true)
    setError(null)
    try {
      await putConteudoChave(CONTEUDO_CHAVES.servicos_intro, intro.trim())
      notifySaved('Texto introdutório guardado.')
    } catch (e) {
      notifyError(e, 'Erro ao salvar')
      setError(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setSavingIntro(false)
    }
  }

  const resetForm = () => {
    setEditing(null)
    setForm(FORM_EMPTY)
  }

  const handleSubmitServico = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.nome.trim()) {
      notifyValidation('Indique o nome do serviço.')
      return
    }
    setSavingCatalog(true)
    setError(null)
    try {
      const precoNum = form.preco.trim() === '' ? null : Number(String(form.preco).replace(',', '.'))
      const duracaoNum = Number(form.duracao_minutos) || 60
      if (precoNum != null && (!Number.isFinite(precoNum) || precoNum < 0)) {
        notifyValidation('Indique um preço válido (ou deixe em branco).')
        setSavingCatalog(false)
        return
      }
      if (duracaoNum < 5 || duracaoNum > 24 * 60) {
        notifyValidation('A duração deve estar entre 5 e 1440 minutos.')
        setSavingCatalog(false)
        return
      }
      if (editing) {
        await updateServico(editing.id, {
          nome: form.nome.trim(),
          descricao: form.descricao.trim() || undefined,
          categoria: form.categoria.trim() || undefined,
          icone: form.icone.trim() || undefined,
          ativo: form.ativo,
          ordem: form.ordem,
          preco: precoNum,
          duracao_minutos: duracaoNum,
        })
        notifySaved('Serviço actualizado.')
      } else {
        await createServico({
          nome: form.nome.trim(),
          descricao: form.descricao.trim() || undefined,
          categoria: form.categoria.trim() || undefined,
          icone: form.icone.trim() || undefined,
          ativo: form.ativo,
          ordem: form.ordem,
          preco: precoNum,
          duracao_minutos: duracaoNum,
        })
        notifySaved('Serviço criado.')
      }
      resetForm()
      load()
    } catch (e) {
      notifyError(e, 'Erro ao salvar')
      setError(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setSavingCatalog(false)
    }
  }

  const handleEdit = (s: Servico) => {
    setEditing(s)
    setForm({
      nome: s.nome,
      descricao: s.descricao ?? '',
      categoria: s.categoria ?? '',
      icone: s.icone ?? '',
      ativo: !!s.ativo,
      ordem: s.ordem ?? 0,
      preco: s.preco != null && s.preco !== '' ? String(s.preco) : '',
      duracao_minutos: String(s.duracao_minutos ?? 60),
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Excluir este serviço do catálogo?')) return
    setError(null)
    try {
      await deleteServico(id)
      if (editing?.id === id) resetForm()
      load()
      notifyDeleted('Serviço removido.')
    } catch (e) {
      notifyError(e, 'Erro ao excluir')
      setError(e instanceof Error ? e.message : 'Erro ao excluir')
    }
  }

  return (
    <AdminPage
      title="Serviços"
      subtitle="Catálogo de serviços usado no agendamento e apresentação no app do cliente."
      loading={loading}
      error={error}
      wide
    >
      <p className={styles.pageHint}>
        Os serviços deste catálogo são utilizados na criação de agendamentos. Mantenha nomes claros e
        categorias alinhadas com a secção de categorias.
      </p>

      {categorias.length === 0 && (
        <p className={styles.pageHint}>
          Ainda não há categorias. <Link to="/admin/categorias">Criar categorias</Link> antes de associar
          aos serviços.
        </p>
      )}

      <form className={styles.adminFormStack} onSubmit={handleSalvarIntro}>
        <AdminPanel
          title="Texto introdutório"
          description="Apresentação da secção de serviços no app do cliente (conteúdo servicos_intro)."
          footer={
            <button type="submit" className={styles.btnSalvar} disabled={savingIntro}>
              {savingIntro ? 'A guardar…' : 'Guardar introdução'}
            </button>
          }
        >
          <textarea
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            placeholder="Breve texto de apresentação da secção de serviços…"
            rows={4}
            className={styles.editorialTextarea}
            style={{ minHeight: '120px' }}
          />
        </AdminPanel>
      </form>

      <form id="form-servico-catalog" className={styles.adminFormStack} onSubmit={handleSubmitServico}>
        <AdminPanel
          title={editing ? `Editar — ${editing.nome}` : 'Novo serviço'}
          description="Nome, preço, duração e categoria — usados na marcação online."
          footer={
            <div className={styles.formActions} style={{ margin: 0 }}>
              <button type="submit" className={styles.btnSalvar} disabled={savingCatalog}>
                {savingCatalog ? 'A guardar…' : editing ? 'Guardar alterações' : 'Adicionar serviço'}
              </button>
              {editing && (
                <button type="button" className={styles.btnCancel} onClick={resetForm}>
                  Cancelar
                </button>
              )}
            </div>
          }
        >
          <div className={styles.formGroup}>
            <label htmlFor="svc-nome">Nome *</label>
            <input
              id="svc-nome"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              placeholder="Nome do serviço"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="svc-desc">Descrição</label>
            <textarea
              id="svc-desc"
              value={form.descricao}
              onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
              rows={2}
              placeholder="Descrição opcional"
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="svc-preco">Preço (€)</label>
            <input
              id="svc-preco"
              inputMode="decimal"
              value={form.preco}
              onChange={(e) => setForm((f) => ({ ...f, preco: e.target.value }))}
              placeholder="ex.: 15,00"
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="svc-duracao">Duração (minutos) *</label>
            <input
              id="svc-duracao"
              type="number"
              min={5}
              max={1440}
              step={5}
              value={form.duracao_minutos}
              onChange={(e) => setForm((f) => ({ ...f, duracao_minutos: e.target.value }))}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="svc-cat">Categoria</label>
            <select
              id="svc-cat"
              value={form.categoria}
              onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))}
              className={styles.servicoCategoriaSelect}
            >
              <option value="">— Sem categoria —</option>
              {categorias.map((c) => (
                <option key={c.nome} value={c.nome}>
                  {c.nome}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="svc-ordem">Ordem</label>
            <input
              id="svc-ordem"
              type="number"
              value={form.ordem}
              onChange={(e) => setForm((f) => ({ ...f, ordem: Number(e.target.value) || 0 }))}
            />
          </div>
          <div className={`${styles.formGroup} ${styles.formRow}`}>
            <input
              id="svc-ativo"
              type="checkbox"
              checked={form.ativo}
              onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
            />
            <label htmlFor="svc-ativo">Activo</label>
          </div>
        </AdminPanel>
      </form>

      <AdminPanel title={`Catálogo (${items.length})`}>
        {items.length === 0 ? (
          <p className={styles.listEmpty}>Nenhum serviço no catálogo. Adicione um acima.</p>
        ) : (
          <div className={styles.listScrollArea}>
            <ul className={styles.list}>
            {items.map((s) => (
              <li key={s.id} className={styles.listItem}>
                <div className={styles.listItemContent}>
                  <div className={styles.listItemTitle}>
                    {s.nome}
                    {s.categoria && (
                      <span style={{ marginLeft: 8, fontWeight: 500, color: 'var(--text-muted)' }}>
                        · {s.categoria}
                      </span>
                    )}
                  </div>
                  <div className={styles.listItemMeta}>
                    {s.duracao_minutos != null ? `${s.duracao_minutos} min` : '60 min'}
                    {formatPrecoDisplay(s.preco) ? ` · ${formatPrecoDisplay(s.preco)} €` : ''}
                  </div>
                  {s.descricao && <div className={styles.listItemMeta}>{s.descricao}</div>}
                  {!s.ativo && <span className={styles.listItemMeta}>(inactivo)</span>}
                </div>
                <div className={styles.listItemActions}>
                  <button type="button" className={styles.btnSecondary} onClick={() => handleEdit(s)}>
                    Editar
                  </button>
                  <button type="button" className={styles.btnDanger} onClick={() => handleDelete(s.id)}>
                    Excluir
                  </button>
                </div>
              </li>
            ))}
            </ul>
          </div>
        )}
      </AdminPanel>
    </AdminPage>
  )
}
